import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { authAPI } from '../../services/api';
import { toast } from 'react-toastify';
import {
    UserCircleIcon,
    KeyIcon,
    EyeIcon,
    EyeSlashIcon,
    ShieldCheckIcon,
    InformationCircleIcon,
    CheckCircleIcon
} from '@heroicons/react/24/outline';

const UserProfile = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
    });

    const [errors, setErrors] = useState({});

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!passwordData.currentPassword) {
            newErrors.currentPassword = 'Current password is required';
        }

        if (!passwordData.newPassword) {
            newErrors.newPassword = 'New password is required';
        } else if (passwordData.newPassword.length < 6) {
            newErrors.newPassword = 'New password must be at least 6 characters long';
        }

        if (!passwordData.confirmNewPassword) {
            newErrors.confirmNewPassword = 'Please confirm your new password';
        } else if (passwordData.newPassword !== passwordData.confirmNewPassword) {
            newErrors.confirmNewPassword = 'New password confirmation does not match';
        }

        if (passwordData.currentPassword && passwordData.newPassword &&
            passwordData.currentPassword === passwordData.newPassword) {
            newErrors.newPassword = 'New password must be different from current password';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            await authAPI.changePassword(passwordData);
            toast.success('Password changed successfully!');

            // Clear form
            setPasswordData({
                currentPassword: '',
                newPassword: '',
                confirmNewPassword: ''
            });
            setErrors({});
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.error('Change password error:', error);
            }
            setErrors(error.response?.data?.message || 'Failed to change password');
        } finally {
            setLoading(false);
        }
    };

    const getPasswordStrength = (password) => {
        if (!password) return { level: 0, text: '', color: 'gray' };

        let score = 0;
        if (password.length >= 8) score++;
        if (/[a-z]/.test(password)) score++;
        if (/[A-Z]/.test(password)) score++;
        if (/[0-9]/.test(password)) score++;
        if (/[^a-zA-Z0-9]/.test(password)) score++;

        if (score < 2) return { level: 1, text: 'Weak', color: 'red' };
        if (score < 4) return { level: 2, text: 'Medium', color: 'yellow' };
        return { level: 3, text: 'Strong', color: 'green' };
    };

    const passwordStrength = getPasswordStrength(passwordData.newPassword);

    return (
        <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm border border-soft-200 p-6 mb-6">
                <div className="flex items-center space-x-4">
                    <div className="h-16 w-16 bg-gradient-to-r from-primary-400 to-secondary-400 rounded-full flex items-center justify-center shadow-lg">
                        <UserCircleIcon className="h-10 w-10 text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-soft-900">Profile Settings</h1>
                        <p className="text-soft-600">Manage your account settings and security</p>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Profile Information */}
                <div className="lg:col-span-1">
                    <div className="bg-white rounded-lg shadow-sm border border-soft-200 p-6">
                        <h2 className="text-lg font-semibold text-soft-900 mb-4 flex items-center space-x-2">
                            <InformationCircleIcon className="h-5 w-5 text-primary-600" />
                            <span>Account Information</span>
                        </h2>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-soft-700 mb-1">Name</label>
                                <div className="text-soft-900 font-medium">{user?.name}</div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-soft-700 mb-1">Email</label>
                                <div className="text-soft-900">{user?.email}</div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-soft-700 mb-1">Role</label>
                                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${user?.role === 'admin'
                                    ? 'bg-secondary-100 text-secondary-700 border border-secondary-200'
                                    : 'bg-primary-100 text-primary-700 border border-primary-200'
                                    }`}>
                                    {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
                                </span>
                            </div>

                            {/* Credits Information */}
                            <div className="pt-4 border-t border-soft-200">
                                <h3 className="text-sm font-medium text-soft-700 mb-3">Available Credits</h3>
                                <div className="space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-soft-600">SMS Credits:</span>
                                        <span className="font-medium text-soft-900">{user?.smsCredits?.toLocaleString() || 0}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-soft-600">WhatsApp Credits:</span>
                                        <span className="font-medium text-soft-900">{user?.whatsappCredits?.toLocaleString() || 0}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-soft-600">Voice Call Credits:</span>
                                        <span className="font-medium text-soft-900">{user?.voiceCallCredits?.toLocaleString() || 0}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Change Password Form */}
                <div className="lg:col-span-2">
                    <div className="bg-white rounded-lg shadow-sm border border-soft-200 p-6">
                        <h2 className="text-lg font-semibold text-soft-900 mb-4 flex items-center space-x-2">
                            <KeyIcon className="h-5 w-5 text-primary-600" />
                            <span>Change Password</span>
                        </h2>

                        {/* Security Notice */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                            <div className="flex items-start space-x-3">
                                <ShieldCheckIcon className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                                <div>
                                    <h4 className="text-sm font-medium text-blue-800 mb-1">Security Recommendations</h4>
                                    <ul className="text-sm text-blue-700 space-y-1">
                                        <li>• Use at least 8 characters</li>
                                        <li>• Include uppercase and lowercase letters</li>
                                        <li>• Add numbers and special characters</li>
                                        <li>• Avoid using personal information</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
                            {/* Current Password */}
                            <div>
                                <label className="block text-sm font-medium text-soft-700 mb-2">
                                    Current Password *
                                </label>
                                <div className="relative">
                                    <input
                                        type={showCurrentPassword ? "text" : "password"}
                                        name="currentPassword"
                                        value={passwordData.currentPassword}
                                        onChange={handleInputChange}
                                        className={`input-field pr-10 ${errors.currentPassword ? 'border-red-500' : ''}`}
                                        placeholder="Enter your current password"
                                        disabled={loading}
                                        autoComplete="current-password"
                                        autoCorrect="off"
                                        autoCapitalize="off"
                                        spellCheck="false"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-soft-400 hover:text-soft-600"
                                    >
                                        {showCurrentPassword ? (
                                            <EyeSlashIcon className="h-5 w-5" />
                                        ) : (
                                            <EyeIcon className="h-5 w-5" />
                                        )}
                                    </button>
                                </div>
                                {errors.currentPassword && (
                                    <p className="text-red-600 text-sm mt-1">{errors.currentPassword}</p>
                                )}
                            </div>

                            {/* New Password */}
                            <div>
                                <label className="block text-sm font-medium text-soft-700 mb-2">
                                    New Password *
                                </label>
                                <div className="relative">
                                    <input
                                        type={showNewPassword ? "text" : "password"}
                                        name="newPassword"
                                        value={passwordData.newPassword}
                                        onChange={handleInputChange}
                                        className={`input-field pr-10 ${errors.newPassword ? 'border-red-500' : ''}`}
                                        placeholder="Enter your new password"
                                        disabled={loading}
                                        autoComplete="new-password"
                                        autoCorrect="off"
                                        autoCapitalize="off"
                                        spellCheck="false"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-soft-400 hover:text-soft-600"
                                    >
                                        {showNewPassword ? (
                                            <EyeSlashIcon className="h-5 w-5" />
                                        ) : (
                                            <EyeIcon className="h-5 w-5" />
                                        )}
                                    </button>
                                </div>

                                {/* Password Strength Indicator */}
                                {passwordData.newPassword && (
                                    <div className="mt-2">
                                        <div className="flex items-center justify-between text-sm mb-1">
                                            <span className="text-soft-600">Password Strength:</span>
                                            <span className={`font-medium ${passwordStrength.color === 'red' ? 'text-red-600' :
                                                passwordStrength.color === 'yellow' ? 'text-yellow-600' :
                                                    'text-green-600'
                                                }`}>
                                                {passwordStrength.text}
                                            </span>
                                        </div>
                                        <div className="h-2 bg-gray-200 rounded-full">
                                            <div
                                                className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.color === 'red' ? 'bg-red-500' :
                                                    passwordStrength.color === 'yellow' ? 'bg-yellow-500' :
                                                        'bg-green-500'
                                                    }`}
                                                style={{ width: `${(passwordStrength.level / 3) * 100}%` }}
                                            />
                                        </div>
                                    </div>
                                )}

                                {errors.newPassword && (
                                    <p className="text-red-600 text-sm mt-1">{errors.newPassword}</p>
                                )}
                            </div>

                            {/* Confirm New Password */}
                            <div>
                                <label className="block text-sm font-medium text-soft-700 mb-2">
                                    Confirm New Password *
                                </label>
                                <div className="relative">
                                    <input
                                        type={showConfirmPassword ? "text" : "password"}
                                        name="confirmNewPassword"
                                        value={passwordData.confirmNewPassword}
                                        onChange={handleInputChange}
                                        className={`input-field pr-10 ${errors.confirmNewPassword ? 'border-red-500' : ''}`}
                                        placeholder="Confirm your new password"
                                        disabled={loading}
                                        autoComplete="new-password"
                                        autoCorrect="off"
                                        autoCapitalize="off"
                                        spellCheck="false"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-soft-400 hover:text-soft-600"
                                    >
                                        {showConfirmPassword ? (
                                            <EyeSlashIcon className="h-5 w-5" />
                                        ) : (
                                            <EyeIcon className="h-5 w-5" />
                                        )}
                                    </button>
                                </div>

                                {/* Password Match Indicator */}
                                {passwordData.confirmNewPassword && passwordData.newPassword && (
                                    <div className="flex items-center mt-2 text-sm">
                                        {passwordData.newPassword === passwordData.confirmNewPassword ? (
                                            <>
                                                <CheckCircleIcon className="h-4 w-4 text-green-600 mr-1" />
                                                <span className="text-green-600">Passwords match</span>
                                            </>
                                        ) : (
                                            <>
                                                <InformationCircleIcon className="h-4 w-4 text-yellow-600 mr-1" />
                                                <span className="text-yellow-600">Passwords do not match</span>
                                            </>
                                        )}
                                    </div>
                                )}

                                {errors.confirmNewPassword && (
                                    <p className="text-red-600 text-sm mt-1">{errors.confirmNewPassword}</p>
                                )}
                            </div>

                            {/* Submit Button */}
                            <div className="pt-4 border-t border-soft-200">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 w-full sm:w-auto"
                                >
                                    {loading ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                            <span className="hidden sm:inline">Changing Password...</span>
                                            <span className="sm:hidden">Changing...</span>
                                        </>
                                    ) : (
                                        <>
                                            <KeyIcon className="h-4 w-4" />
                                            <span className="hidden sm:inline">Change Password</span>
                                            <span className="sm:hidden">Change</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserProfile; 